import * as THREE from 'three';
import { AppManager } from '@common';

const factor = 4;
const canvasWidth = 512 * factor;
const canvasHeight = 32 * factor;

function roundRect(ctx, x, y, w, h, r) {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.lineTo(x + w - r, y);
  ctx.quadraticCurveTo(x + w, y, x + w, y + r);
  ctx.lineTo(x + w, y + h - r);
  ctx.quadraticCurveTo(x + w, y + h, x + w - r, y + h);
  ctx.lineTo(x + r, y + h);
  ctx.quadraticCurveTo(x, y + h, x, y + h - r);
  ctx.lineTo(x, y + r);
  ctx.quadraticCurveTo(x, y, x + r, y);
  ctx.closePath();
  ctx.fill();
}


function getFontFamily() {
  switch (AppManager.currentLanguage) {
    case 'ja':
      return 'font-ja';
    case 'zh-Hant':
      return 'font-zh-Hant';
    case 'zh-Hans':
      return 'font-zh-Hans';
    case 'en':
    default:
      return 'font-en';
  }
}


// 透明ピクセルをトリムしてコピー
// https://gist.github.com/remy/784508
function trim(c) {
  var ctx = c.getContext('2d'),
    copy = document.createElement('canvas').getContext('2d'),
    pixels = ctx.getImageData(0, 0, c.width, c.height),
    l = pixels.data.length,
    i,
    bound = {
      top: null,
      left: null,
      right: null,
      bottom: null
    },
    x, y;

  for (i = 0; i < l; i += 4) {
    if (pixels.data[i + 3] !== 0) {
      x = (i / 4) % c.width;
      y = ~~((i / 4) / c.width);

      if (bound.top === null) {
        bound.top = y;
      }

      if (bound.left === null) {
        bound.left = x;
      } else if (x < bound.left) {
        bound.left = x;
      }

      if (bound.right === null) {
        bound.right = x;
      } else if (bound.right < x) {
        bound.right = x;
      }

      if (bound.bottom === null) {
        bound.bottom = y;
      } else if (bound.bottom < y) {
        bound.bottom = y;
      }
    }
  }

  var trimHeight = bound.bottom - bound.top,
    trimWidth = bound.right - bound.left,
    trimmed = ctx.getImageData(bound.left, bound.top, trimWidth, trimHeight);

  copy.canvas.width = trimWidth;
  copy.canvas.height = trimHeight;
  copy.putImageData(trimmed, 0, 0);

  // open new window with trimmed image:
  return copy.canvas;
}


export const textSpriteUpdateScale = (sprite, scale) => {
  const ratio = sprite.material.map.image.width / sprite.material.map.image.height;
  sprite.scale.set(ratio * scale, scale, 1.0);
};


// 参考
// https://gist.github.com/studioijeoma/1186920/5f50d999aa07491e67ec60d05e892637185cd4cd
export const textSprite = (label, scale, style = {}) => {
  const sprite = new THREE.Sprite(textSpriteMaterial(label, style));
  textSpriteUpdateScale(sprite, scale);
  return sprite;
};


export const textSpriteMaterial = (label, style = {}) => {
  const texture = textSpriteTexture(label, style);
  const spriteMaterial = new THREE.SpriteMaterial({
    map: texture,
    depthTest: false,
  });
  return spriteMaterial;
};


export const textSpriteTexture = (label, style = {}) => {
  const canvas2 = document.createElement('canvas');
  canvas2.width = canvasWidth;
  canvas2.height = canvasHeight;
  const context = canvas2.getContext('2d');

  if (style.fontSize === undefined) style.fontSize = 18;
  if (style.fontWeight === undefined) style.fontWeight = 'normal';
  const {
    fontSize,
    fontWeight,
    fontFamily,
    backgroundColor,
    backgroundStyle,
    fixedWidth,
    fixedHeight,
    fillStyle,
    bullet,
    textAlign,
    borderThickness,
    strokeStyle,
    trimCanvas,
  } = style;

  context.font = `${fontWeight} ${fontSize}px ${fontFamily ? fontFamily : getFontFamily()}`;
  context.textAlign = textAlign ? textAlign : 'center';
  context.textBaseline = 'middle';

  const metrics = context.measureText(label);
  const textWidth = fixedWidth ? fixedWidth : metrics.width;
  const padH = fontSize * 0.5;
  const padV = fontSize * 0.4;
  const textHeight = fixedHeight ? fixedHeight : fontSize;

  const rx = (canvas2.width - textWidth) / 2 - padH;
  const ry = (canvas2.height - textHeight) / 2 - padV;
  const rw = textWidth + padH * 2;
  const rh = textHeight + padV * 2;

  if (backgroundColor) {
    context.fillStyle = backgroundColor;
    if (backgroundStyle && backgroundStyle == 'round') {
      roundRect(context,
        rx,
        ry,
        rw,
        rh,
        rh / 2);
    } else {
      context.fillRect(
        rx,
        ry,
        rw,
        rh
      );
      if (borderThickness && borderThickness > 0) {
        context.lineWidth = borderThickness;
        context.strokeStyle = strokeStyle ? strokeStyle : 'rgba(0, 0, 0, 1.0)';
        context.strokeRect(
          rx,
          ry,
          rw - borderThickness * 2,
          rh - borderThickness * 2
        );
      }
    }
  }
  const merginEmH = 2;
  context.fillStyle = fillStyle ? fillStyle : 'rgba(0, 0, 0, 1.0)';
  if (context.textAlign === 'center') {
    context.fillText(label, canvas2.width / 2, canvas2.height / 2);
  } else {
    // left
    context.fillText(label, rx + fontSize * merginEmH, canvas2.height / 2);
  }

  if (bullet) {
    const xEdge = fixedWidth ? rx : 0;
    context.fillText(bullet, xEdge + rw - fontSize * merginEmH, canvas2.height / 2);
  }

  const trimming = trimCanvas === undefined ? true : false; 
  const texture = trimming ? new THREE.CanvasTexture(trim(canvas2)) : new THREE.CanvasTexture(canvas2);
  texture.needsUpdate = true;
  return texture;
};